import React, { useEffect, useRef } from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  Routes,
  Route,
} from 'react-router-dom';
import ProtectedRoute from 'components/services/ProtectedRoute';
import SelectOrg from 'components/templates/SelectOrg';
import { Main } from 'components/templates';

interface ProtectedMainProps {
  tool: any;  // Consider creating a proper type for tool
}

// Define a component that wraps Main with ProtectedRoute
const ProtectedMain: React.FC<ProtectedMainProps> = ({ tool }) => (
  <ProtectedRoute>
    <Main tool={tool} />
  </ProtectedRoute>
);
import SigninPage from 'components/templates/SigninPage';
import AuthCallback from 'components/templates/AuthCallback';
import DesignsList from 'components/templates/DesignsList';
import ModernUIDemo from 'components/templates/ModernUIDemo';
import NavContainerDemo from 'components/templates/NavContainerDemo';
import FloorplanDemo from 'components/templates/FloorplanDemo';
import toolIndex from 'toolConstants';
import { values } from 'lodash';
import { useAuth } from './contexts/AuthContext';
import { useTheme } from './theme/ThemeContext';
import { fetchOrganizations } from './services/orgService';

const App: React.FC = () => {
  const { pathname, search } = useLocation();
  const [isAuthChecked, setIsAuthChecked] = React.useState(false);
  const { mode } = useTheme();
  const tool = values(toolIndex).find(
    (tool) => pathname.includes(tool.constants.tool.path.long)
  );
  // Extract tool forwarding parameter from URL if present
  const regexMatch = /(?<=key=)(?<toolFwd>[a-z]*)(?=-[\w\d])/.exec(search);
  const toolFwd = regexMatch?.groups?.toolFwd;

  // Get auth context for redirect handling
  const { user, loading, isAuthenticated, redirectReady, redirectPath, setRedirectReady, setRedirectPath } =
    useAuth();

  const navigate = useNavigate();

  // Handle tool forwarding from URL parameters
  useEffect(() => {
    if (!toolFwd || pathname.includes(toolFwd)) return;
    navigate({
      pathname: pathname === '/' ? toolFwd : pathname,
      search: search
    });
  }, [toolFwd, navigate, pathname, search]);

  // Use ref to prevent multiple redirects
  const isRedirecting = useRef<boolean>(false);

  // Handle redirect after authentication
  useEffect(() => {
    if (redirectReady && redirectPath && !isRedirecting.current) {
      // Set flag to prevent multiple redirects
      isRedirecting.current = true;

      // Use setTimeout to ensure we don't trigger too many redirects
      setTimeout(() => {
        navigate(redirectPath);

        // Reset redirect state
        setRedirectReady(false);
        setRedirectPath(null);

        // Reset the flag after a delay to allow the navigation to complete
        setTimeout(() => {
          isRedirecting.current = false;
        }, 1000);
      }, 100);
    }
  }, [redirectReady, redirectPath, navigate, setRedirectReady, setRedirectPath]);

  // Root redirect logic - redirect to designs or conference room designer
  useEffect(() => {
    const handleRootRedirect = async () => {
      // Check if authentication is disabled
      const isAuthDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true';
      
      // Only handle redirect for root path and only when not loading
      if (pathname === '/' && !loading) {
        try {
          // If auth is disabled, redirect to conference room designer
          if (isAuthDisabled) {
            navigate('/conference_room_designer');
            setIsAuthChecked(true);
            return;
          }
          
          const authenticated = await isAuthenticated();
          if (authenticated) {
            const params = new URLSearchParams(search);
            const orgIdFromPicker = params.get('org_id');
            const membershipIdFromPicker = params.get('membership_id');
            const regionFromPicker = params.get('region');

            if (orgIdFromPicker) {
              sessionStorage.setItem('selectedOrgId', orgIdFromPicker);
              if (membershipIdFromPicker) sessionStorage.setItem('selectedMembershipId', membershipIdFromPicker);
              if (regionFromPicker) sessionStorage.setItem('selectedRegion', regionFromPicker);

              try {
                const organizations = await fetchOrganizations();
                const org = organizations.find(o => o.id === orgIdFromPicker);
                if (org) {
                  sessionStorage.setItem('selectedOrgName', org.name);
                  if (org.domain) sessionStorage.setItem('selectedOrgDomain', org.domain);
                }
              } catch (err) {
                console.warn('Failed to fetch org info after Workplace return:', err);
              }

              window.history.replaceState({}, document.title, window.location.pathname);
              navigate('/designs');
              setIsAuthChecked(true);
              return;
            }

            const ssoAuthenticated = localStorage.getItem('sso_authenticated');
            if (ssoAuthenticated === 'true') {
              localStorage.removeItem('sso_authenticated');
              console.log('SSO authentication detected, redirecting to designs');
              navigate('/designs');
            } else {
              const workplaceHomeUrl = process.env.REACT_APP_WORKPLACE_HOME_URL || 'https://stage.workplace.biamp.app';
              const redirectUrl = encodeURIComponent(window.location.origin);
              window.location.href = `${workplaceHomeUrl}/external/organizations?redirect=${redirectUrl}`;
            }
          } else {
            navigate('/signin');
          }
        } catch (error) {
          console.error('Error checking authentication for root redirect:', error);
          // On error, redirect to sign in to be safe (unless auth is disabled)
          if (!isAuthDisabled) {
            navigate('/signin');
          }
        } finally {
          setIsAuthChecked(true);
        }
      } else if (pathname !== '/' && pathname !== '/signin') {
        // For non-root paths, just mark auth as checked without redirecting
        setIsAuthChecked(true);
      }
    };

    handleRootRedirect();
  }, [pathname, search, loading, isAuthenticated, navigate]);

  // Check user state directly for immediate logout detection (but not on page refresh)
  useEffect(() => {
    const isAuthDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true';
    // Only redirect if we're not loading, user is null, not on sign in page, and not on root path
    // The root path has its own redirect logic above
    if (!isAuthDisabled && !loading && !user && pathname !== '/signin' && pathname !== '/' && isAuthChecked) {
      navigate('/signin');
    }
  }, [user, loading, pathname, navigate, isAuthChecked]);

  // Additional authentication check for non-root paths - but only after initial auth check
  useEffect(() => {
    const checkAuth = async () => {
      const isAuthDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true';
      if (!isAuthDisabled && !loading && pathname !== '/' && pathname !== '/signin' && isAuthChecked) {
        const authenticated = await isAuthenticated();
        if (!authenticated) {
          navigate('/signin');
        }
      }
    };

    // Only run this check if we're not on initial load
    if (isAuthChecked) {
      checkAuth();
    }
  }, [loading, pathname, isAuthenticated, navigate, isAuthChecked]);

  // If we're on the sign in page, render the sign in page directly
  if (pathname === '/signin') {
    return <SigninPage />;
  }

  // Show loading state while authentication is being checked or while on root path
  if ((!isAuthChecked && pathname !== '/signin' && pathname !== '/selectorg' && pathname !== '/designs') || 
      (pathname === '/' && !loading)) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='d-flex flex-column vh-100'>
      <div className="flex-grow-1 d-flex">
        <Routes>
        <Route path="auth/signin" element={<AuthCallback />} />
        <Route path="selectorg" element={<ProtectedRoute><SelectOrg /></ProtectedRoute>} />
        <Route path="designs" element={<ProtectedRoute><DesignsList /></ProtectedRoute>} />
        <Route path="modern-ui" element={<ModernUIDemo />} />
        <Route path="nav-demo" element={<NavContainerDemo />} />
        <Route path="floorplan-demo" element={<FloorplanDemo />} />
          {values(toolIndex).map((tool, _) => (
            <Route 
              path={tool.constants.tool.path.long} 
              key={`${tool.constants.tool.name}-long`} 
              element={<ProtectedMain tool={tool} />} 
            />
          ))}
          {values(toolIndex).map((tool, _) => (
            <Route 
              path={tool.constants.tool.path.short} 
              key={`${tool.constants.tool.name}-short`} 
              element={<ProtectedMain tool={tool} />} 
            />
          ))}
          <Route path="*" element={<Outlet />} />
        </Routes>
      </div>
    </div>
  );
};

export default App;
